import dayjs from 'dayjs'
import api from '@/api'
import { _surfaceErrorMessage, _surfaceSuccessMessage } from '@/snackbar'
import { PLACED, ENTITIES } from '../constants'
import { compareByEndDateDesc } from '../helpers'

export const getters = {
  getFutureProposedClientsForUser: (state) => (userId) =>
    state[ENTITIES.PLACEMENTS]
      .filter((placement) => placement.userId === userId)
      .filter(
        (placement) =>
          placement.status !== PLACED ||
          dayjs(state[ENTITIES.STAFFING_ROLES_BY_ID][placement.staffingRoleId]?.startDate).isAfter(
            dayjs(),
            'day'
          )
      )
      .filter(
        (placement) =>
          state[ENTITIES.ENGAGEMENTS_BY_ID][
            state[ENTITIES.STAFFING_ROLES_BY_ID][placement.staffingRoleId]?.engagementId
          ]?.likelihood > 0
      )
      .map((placement) => ({
        clientName:
          state[ENTITIES.CLIENTS_BY_ID][
            state[ENTITIES.ENGAGEMENTS_BY_ID][
              state[ENTITIES.STAFFING_ROLES_BY_ID][placement.staffingRoleId]?.engagementId
            ]?.clientId
          ]?.name,
        status: placement.status,
        likelihood:
          state[ENTITIES.ENGAGEMENTS_BY_ID][
            state[ENTITIES.STAFFING_ROLES_BY_ID][placement.staffingRoleId]?.engagementId
          ]?.likelihood,
        startDate: dayjs(
          state[ENTITIES.STAFFING_ROLES_BY_ID][placement.staffingRoleId]?.startDate
        ).format('MM/DD/YY'),
        endDate: dayjs(
          state[ENTITIES.STAFFING_ROLES_BY_ID][placement.staffingRoleId]?.endDate
        ).format('MM/DD/YY'),
      })) || [],

  getActiveClients: (state) => () => state[ENTITIES.CLIENTS].filter((client) => !client.archived),

  getArchivedClients: (state) => () => state[ENTITIES.CLIENTS].filter((client) => client.archived),

  getCurrentClientNameByClientId() {
    return (clientId) => {
      const currentClient = this.getEntityById(ENTITIES.CLIENTS, clientId)
      return currentClient ? currentClient.name : 'Bench'
    }
  },

  getListOfPastClientsForUser() {
    return (consultantId) => {
      const staffingRoles = this.getAllPastPlacedStaffingRolesByConsultantId(consultantId)
      const pastClients = staffingRoles.map((role) => {
        const engagement = this.getEntityById(ENTITIES.ENGAGEMENTS, role.engagementId)
        const { name } = this.getEntityById(ENTITIES.CLIENTS, engagement.clientId)
        const { id, startDate, endDate } = role
        return { id, name, startDate, endDate }
      })
      return pastClients.sort(compareByEndDateDesc)
    }
  },
}

export const actions = {
  async saveClient(data) {
    try {
      const newClient = await api.saveClient(data)
      this.clients.push(newClient)
      _surfaceSuccessMessage('Added!')
    } catch {
      _surfaceErrorMessage('Creation failed. Please try again.')
    }
  },

  async updateClient(data) {
    try {
      const updatedClient = await api.updateClient(data)
      this.clients = this.clients.map((entity) =>
        entity.id === updatedClient.id ? { ...entity, ...updatedClient } : entity
      )
      _surfaceSuccessMessage('Updated!')
    } catch {
      _surfaceErrorMessage('Update failed! Please try again.')
    }
  },

  async fetchClientsById() {
    this.clientsById = await api.fetchClientsById()
  },

  async fetchClients() {
    const clients = await api.fetchClients()
    const transformedClientsObject = clients.reduce(
      (accumulator, client) => {
        const { engagements, ...clientInfo } = client
        accumulator.clients.push(clientInfo)
        engagements.forEach((engagement) => {
          const { staffingRoles, ...engagementInfo } = engagement
          accumulator.engagements.push(engagementInfo)
          staffingRoles.forEach((staffingRole) => {
            const { placements, ...staffingRoleInfo } = staffingRole
            accumulator.staffingRoles.push(staffingRoleInfo)
            placements.forEach((p) => {
              accumulator.placements.push(p)
            })
          })
        })
        return accumulator
      },
      { clients: [], engagements: [], staffingRoles: [], placements: [] }
    )
    this.clients = transformedClientsObject.clients
    this.engagements = transformedClientsObject.engagements
    this.staffingRoles = transformedClientsObject.staffingRoles
    this.placements = transformedClientsObject.placements
  },

  async fetchActiveClientsById() {
    this.activeClientsById = await api.fetchActiveClientsById()
  },
}
